import type { MarlinStoreDb } from "../infra/db";
import { createCameraOperations } from "../logic/layouts/camera-operations";
import { defaultScreenLayoutValues } from "../logic/layouts/constants";
import { createFeedOperations } from "../logic/layouts/feed-operations";
import { createGridOperations } from "../logic/layouts/grid-operations";
import { createGridUtils } from "../logic/layouts/grid-utils";
import { createMachineOperations } from "../logic/layouts/machine-operations";
import { createOverlayOperations } from "../logic/layouts/overlay-operations";
import { createScreenOperations } from "../logic/layouts/screen-operations";
import { createValidationMethods } from "../logic/layouts/validation";
import { readState, withState, type SyncStore } from "../infra/state";
import type {
  GridPosition,
  LoadMachinesOptions,
  MachinesToLoad,
  OnlyScreenData,
  PinData,
  PositionData,
  ScreenLayout,
  ScreenType,
} from "../types";
import { StoreValidationError } from "../infra/validation";
import { assertValidFeedList } from "./validate";

const LAYOUT_KEYS = [
  "screenLayouts",
  "machinesLoaded",
  "selected",
  "selectedMultiScreenTemplateId",
  "selectedScreenTemplateIds",
] as const;

export const defaultScreen = (id: number): ScreenLayout => ({
  ...structuredClone(defaultScreenLayoutValues),
  id,
});

export const layoutOps = (
  store: SyncStore,
  options: { loadingTemplate?: boolean; loadingScreenTemplateId?: number } = {},
) => {
  const validation = createValidationMethods(store);
  const gridUtils = createGridUtils();
  const validateLayout = (layout: ScreenLayout) =>
    validation.validateLayout(layout, gridUtils.gridToUse(layout));

  const findScreenLayout = (screenId: number): ScreenLayout => {
    const layout = store.get("screenLayouts").find((l) => l.id === screenId);
    if (!layout) throw new Error(`Screen layout with id ${screenId} not found`);
    return layout;
  };

  const writeScreenLayout = (
    screenId: number,
    layout: ScreenLayout,
  ): ScreenLayout => {
    const layouts = store.get("screenLayouts");
    const index = layouts.findIndex((entry) => entry.id === screenId);
    if (index === -1) layouts.push(layout);
    else layouts[index] = layout;
    store.set("screenLayouts", layouts);
    return layout;
  };

  const setScreenLayout = (
    screenId: number,
    layout: ScreenLayout,
  ): ScreenLayout => {
    const layouts = store.get("screenLayouts");
    const index = layouts.findIndex((l) => l.id === screenId);
    if (index !== -1) layouts[index] = layout;
    else layouts.push(layout);
    if (!options.loadingTemplate)
      store.set("selectedMultiScreenTemplateId", null);
    if (screenId !== options.loadingScreenTemplateId) {
      const selected = store.get("selectedScreenTemplateIds");
      if (selected[screenId])
        store.set("selectedScreenTemplateIds", {
          ...selected,
          [screenId]: null,
        });
    }
    store.set("screenLayouts", layouts.map(validateLayout));
    return layout;
  };

  return {
    utils: gridUtils,
    validateLayout,
    findScreenLayout,
    setScreenLayout,
    ...createScreenOperations(store),
    ...createOverlayOperations(
      store,
      findScreenLayout,
      setScreenLayout,
      gridUtils.positionToGridId,
    ),
    ...createGridOperations(
      store,
      findScreenLayout,
      setScreenLayout,
      gridUtils.shouldUseCameraGrid,
      gridUtils.gridToUse,
      gridUtils.positionToGridId,
      validation.isValidGridLayout,
      validation.validateCagePositionData,
      validation.validateCameraPositionData,
      gridUtils.adjustFocusedPositionsForNewLayout,
      gridUtils.findExistingCameraPosition,
      gridUtils.findExistingPinPosition,
    ),
    ...createCameraOperations(
      store,
      findScreenLayout,
      setScreenLayout,
      gridUtils.shouldUseCameraGrid,
      gridUtils.gridToUse,
      gridUtils.positionToGridId,
      gridUtils.findExistingCameraPosition,
    ),
    ...createMachineOperations(
      store,
      validation.validateLayout,
      gridUtils.gridToUse,
    ),
    ...createFeedOperations(findScreenLayout, writeScreenLayout),
  };
};

export type LayoutOperations = ReturnType<typeof layoutOps>;

type Ops = LayoutOperations;

const edit = <R>(db: MarlinStoreDb, fn: (ops: Ops) => R): Promise<Awaited<R>> =>
  withState(db, LAYOUT_KEYS, (store) => fn(layoutOps(store)));

const readOnlyStore = (screenLayouts: ScreenLayout[]): SyncStore => ({
  get: (key) => {
    if (key !== "screenLayouts")
      throw new Error(`a layout read touched state key ${key}`);
    return screenLayouts as never;
  },
  set: () => {
    throw new Error("read-only snapshot");
  },
});

const view = async <R>(
  db: MarlinStoreDb,
  fn: (ops: Ops) => R,
): Promise<Awaited<R>> => {
  const { screenLayouts } = await readState(db, ["screenLayouts"]);
  return await fn(layoutOps(readOnlyStore(screenLayouts)));
};

const viewScreen = async <R>(
  db: MarlinStoreDb,
  screenId: number,
  fn: (ops: Ops) => R,
): Promise<Awaited<R>> => {
  const layout = await getScreenLayout(db, screenId);
  return await fn(layoutOps(readOnlyStore([layout])));
};

export const getScreenLayouts = (db: MarlinStoreDb): Promise<ScreenLayout[]> =>
  db.screens.orderBy("id").toArray();

export const getScreenLayout = async (
  db: MarlinStoreDb,
  screenId: number,
): Promise<ScreenLayout> => {
  const layout = await db.screens.get(screenId);
  if (!layout) throw new Error(`Screen layout with id ${screenId} not found`);
  return layout;
};

export const getScreens = (db: MarlinStoreDb): Promise<OnlyScreenData[]> =>
  view(db, (ops) => ops.getScreens());

export const getScreen = (
  db: MarlinStoreDb,
  screenId: number,
): Promise<OnlyScreenData> =>
  viewScreen(db, screenId, (ops) => ops.getScreen(screenId));

export const getOverlays = (db: MarlinStoreDb, screenId: number) =>
  viewScreen(db, screenId, (ops) => ops.getOverlays(screenId));

export const getGridData = (db: MarlinStoreDb, screenId: number) =>
  viewScreen(db, screenId, (ops) => ops.getGridData(screenId));

export const getPositionData = (
  db: MarlinStoreDb,
  screenId: number,
  position: GridPosition,
) => viewScreen(db, screenId, (ops) => ops.getPositionData(screenId, position));

export const getPinData = (
  db: MarlinStoreDb,
  screenId: number,
  position: GridPosition,
) => viewScreen(db, screenId, (ops) => ops.getPinData(screenId, position));

export const getAllPins = (db: MarlinStoreDb) =>
  view(db, (ops) => ops.getAllPins());

export const getCamerasOnPinnedCells = (db: MarlinStoreDb) =>
  view(db, (ops) => ops.getCamerasOnPinnedCells());

export const getFeedData = (db: MarlinStoreDb, screenId: number) =>
  viewScreen(db, screenId, (ops) => ops.getFeedData(screenId));

export const setScreens = async (
  db: MarlinStoreDb,
  screens: { id: number; isShowing?: boolean; screenType?: ScreenType }[],
): Promise<void> => {
  if (screens.length === 0)
    throw new StoreValidationError(
      "setScreens needs a stated-complete display list",
    );
  await edit(db, (ops) => ops.setScreens(screens));
};

export const updateScreenType = (
  db: MarlinStoreDb,
  screenId: number,
  screenType: string,
) => edit(db, (ops) => ops.updateScreenType(screenId, screenType));

export const resetScreenTypes = (db: MarlinStoreDb) =>
  edit(db, (ops) => ops.resetScreenTypes());

export const saveScreenConfiguration = (
  db: MarlinStoreDb,
  openScreenIds: number[],
) =>
  withState(db, ["screenLayouts"], (store) => {
    const config = store.get("screenLayouts").map((screen) => ({
      ...screen,
      isShowing: openScreenIds.includes(screen.id),
    }));
    store.set("screenLayouts", config);
    return { success: true as const, savedScreens: config };
  });

export const resetScreenConfiguration = (
  db: MarlinStoreDb,
  displays: { id: number }[],
) =>
  edit(db, (ops) => {
    ops.resetScreenConfiguration();
    if (displays.length > 0) ops.setScreens(displays);
    return ops.getScreens();
  });

export const setFullScreen = (
  db: MarlinStoreDb,
  screenId: number,
  position: GridPosition,
) => edit(db, (ops) => ops.setFullScreen(screenId, position));

export const setHalfScreen = (
  db: MarlinStoreDb,
  screenId: number,
  position: GridPosition,
) => edit(db, (ops) => ops.setHalfScreen(screenId, position));

export const setHalfScreenLeft = (
  db: MarlinStoreDb,
  screenId: number,
  position: GridPosition,
) => edit(db, (ops) => ops.setHalfScreenLeft(screenId, position));

export const setHalfScreenRight = (
  db: MarlinStoreDb,
  screenId: number,
  position: GridPosition,
) => edit(db, (ops) => ops.setHalfScreenRight(screenId, position));

export const clearPosition = (
  db: MarlinStoreDb,
  screenId: number,
  position: GridPosition,
) => edit(db, (ops) => ops.clearPosition(screenId, position));

export const setGridLayout = (
  db: MarlinStoreDb,
  screenId: number,
  gridLayout: ScreenLayout["screenData"]["gridLayout"],
) => edit(db, (ops) => ops.setGridLayout(screenId, gridLayout));

export const setNCams = (
  db: MarlinStoreDb,
  screenId: number,
  nCams: ScreenLayout["screenData"]["nCams"],
) => edit(db, (ops) => ops.setNCams(screenId, nCams));

export const setVideoAspectRatio = (
  db: MarlinStoreDb,
  screenId: number,
  ratio: ScreenLayout["screenData"]["videoAspectRatio"],
) => edit(db, (ops) => ops.setVideoAspectRatio(screenId, ratio));

export const setPositionData = (
  db: MarlinStoreDb,
  screenId: number,
  position: GridPosition,
  data: PositionData | null,
) => edit(db, (ops) => ops.setPositionData(screenId, position, data));

export const setPinData = (
  db: MarlinStoreDb,
  screenId: number,
  position: GridPosition,
  pin: PinData | null,
) => edit(db, (ops) => ops.setPinData(screenId, position, pin));

export const swapCamerasInPosition = (
  db: MarlinStoreDb,
  screenId: number,
  position: GridPosition,
  sourceCam: number,
  targetCam: number,
) =>
  edit(db, (ops) =>
    ops.swapCamerasInPosition(screenId, position, sourceCam, targetCam),
  );

export const cycleCamerasInPosition = (
  db: MarlinStoreDb,
  screenId: number,
  position: GridPosition,
) => edit(db, (ops) => ops.cycleCamerasInPosition(screenId, position));

export const cycleToNextPen = (db: MarlinStoreDb, screenId: number) =>
  edit(db, (ops) => ops.cycleToNextPen(screenId));

export const cycleToPreviousPen = (db: MarlinStoreDb, screenId: number) =>
  edit(db, (ops) => ops.cycleToPreviousPen(screenId));

export const loadMachines = (
  db: MarlinStoreDb,
  machinesToLoad: MachinesToLoad,
  options?: LoadMachinesOptions,
) =>
  edit(db, (ops) => {
    const stated: MachinesToLoad = {};
    for (const [id, cages] of Object.entries(machinesToLoad))
      if (cages.length > 0) stated[Number(id)] = cages;
    return ops.loadMachines(stated, {
      ...options,
      knownMachineIds:
        options?.knownMachineIds ?? Object.keys(machinesToLoad).map(Number),
    });
  });

export const updateFeedSites = (
  db: MarlinStoreDb,
  screenId: number,
  sites: { siteIds: number[] },
) =>
  edit(db, (ops) =>
    ops.updateFeedSites(screenId, { siteIds: [...new Set(sites.siteIds)] }),
  );

export const updateFeedColumns = async (
  db: MarlinStoreDb,
  screenId: number,
  columns: string[],
) => {
  assertValidFeedList("columns", columns);
  await edit(db, (ops) =>
    ops.updateFeedColumns(screenId, [...new Set(columns)]),
  );
};

export const updateCopilotFeedColumns = async (
  db: MarlinStoreDb,
  screenId: number,
  columns: string[],
) => {
  assertValidFeedList("copilotColumns", columns);
  await edit(db, (ops) =>
    ops.updateCopilotFeedColumns(screenId, [...new Set(columns)]),
  );
};

export const updateFeedFilters = async (
  db: MarlinStoreDb,
  screenId: number,
  filters: string[],
) => {
  assertValidFeedList("filters", filters);
  await edit(db, (ops) =>
    ops.updateFeedFilters(screenId, [...new Set(filters)]),
  );
};

export const replaceScreenLayouts = (
  store: SyncStore,
  layouts: ScreenLayout[],
): void => {
  const { validateLayout } = createValidationMethods(store);
  const { gridToUse } = createGridUtils();
  store.set(
    "screenLayouts",
    layouts.map((layout) => validateLayout(layout, gridToUse(layout))),
  );
};
